import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./ItemDetails.css";
import "../AddItem/AddNewProduct.css";

import * as actions from "../../store/actions";
import Spinner from "../../containers/UI/Spinner/Spinner";

import Add from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { toast } from "react-toastify";

import { ErrorBoundary } from "../../containers/ErrorBoundary";

// Static country list — same as AddNewProduct
const COUNTRIES = [
  { name: 'Qatar', code: 'QA' },
  { name: 'United Arab Emirates', code: 'AE' },
  { name: 'Saudi Arabia', code: 'SA' },
  { name: 'Kuwait', code: 'KW' },
  { name: 'Bahrain', code: 'BH' },
  { name: 'Oman', code: 'OM' },
  { name: 'Jordan', code: 'JO' },
  { name: 'Egypt', code: 'EG' },
  { name: 'Lebanon', code: 'LB' },
  { name: 'Turkey', code: 'TR' },
  { name: 'India', code: 'IN' },
  { name: 'Pakistan', code: 'PK' },
  { name: 'Bangladesh', code: 'BD' },
  { name: 'Philippines', code: 'PH' },
  { name: 'Thailand', code: 'TH' },
  { name: 'China', code: 'CN' },
  { name: 'Japan', code: 'JP' },
  { name: 'United States', code: 'US' },
  { name: 'United Kingdom', code: 'GB' },
  { name: 'Germany', code: 'DE' },
  { name: 'France', code: 'FR' },
  { name: 'Italy', code: 'IT' },
  { name: 'Spain', code: 'ES' },
  { name: 'Netherlands', code: 'NL' },
  { name: 'Switzerland', code: 'CH' },
  { name: 'Australia', code: 'AU' },
  { name: 'New Zealand', code: 'NZ' },
  { name: 'Canada', code: 'CA' },
  { name: 'Brazil', code: 'BR' },
  { name: 'Mexico', code: 'MX' },
  { name: 'South Korea', code: 'KR' },
  { name: 'Malaysia', code: 'MY' },
  { name: 'Singapore', code: 'SG' },
  { name: 'Indonesia', code: 'ID' },
  { name: 'Vietnam', code: 'VN' },
  { name: 'Sri Lanka', code: 'LK' },
  { name: 'Nepal', code: 'NP' },
];

function getBase64(img, callback) {
  const reader = new FileReader();
  reader.addEventListener("load", () => callback(reader.result));
  reader.readAsDataURL(img);
}

const ItemDetails = () => {
  const { productMerchantId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux selectors
  const itemLoading = useSelector((state) => state.addItem.productMerchantLoading);
  const productMerchantDetails = useSelector((state) => state.addItem.productMerchantDetails);
  const profile = useSelector((state) => state.profile.profileDetails);
  const updateButtonLoading = useSelector((state) => state.addItem.updateButtonLoading);
  const categories = useSelector((state) => state.addItem.categories);
  const brands = useSelector((state) => state.addItem.brands || []);
  const imgUrlArr = useSelector((state) => state.imageUpload.imgUrlArr);
  const loadingImg = useSelector((state) => state.imageUpload.loading);

  // Local state
  const [selectedImage, setSelectedImage] = useState(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    categoryId: 0,
    brandId: null,
    brandName: "",
    origin: "",
    barcode: "",
    unit: "",
    tags: "",
    price: 0,
    discountPercent: 0,
    stockQuantity: 0,
  });

  // Load data on mount
  useEffect(() => {
    dispatch(actions.getBrands());
    dispatch(actions.getCategories());
    dispatch(actions.getProductMerchantDetails(productMerchantId));
  }, [dispatch, productMerchantId]);

  // Update edit form when productMerchantDetails changes
  useEffect(() => {
    if (productMerchantDetails) {
      const product = productMerchantDetails.product || {};
      setEditForm({
        name: product.name || "",
        description: product.description || "",
        categoryId: product.categoryId || 0,
        brandId: product.brandId || null,
        brandName: product.brand?.name || "",
        origin: product.originCountry || "",
        barcode: product.barcode || "",
        unit: product.unit || "pcs",
        tags: "", // Tags not in current schema
        price: parseFloat(productMerchantDetails.price) || 0,
        discountPercent: parseFloat(productMerchantDetails.discountPercent) || 0,
        stockQuantity: productMerchantDetails.stockQuantity || 0,
      });
    }
  }, [productMerchantDetails]);

  const selectImage = useCallback((index) => {
    setSelectedImage(index);
  }, []);

  const openEditModal = useCallback(() => {
    // Reset image array when opening edit modal
    if (productMerchantDetails?.product?.images) {
      const images = Array.isArray(productMerchantDetails.product.images)
        ? productMerchantDetails.product.images
        : [];
      dispatch(actions.imageSetInitialArr(images));
    }
    setIsEditModalOpen(true);
  }, [productMerchantDetails, dispatch]);

  const closeEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    // Clear image array
    dispatch(actions.imageClearArr());
  }, [dispatch]);

  const handleBrandChange = useCallback((event) => {
    const selectedId = event.target.value;
    const selectedBrand = brands.find((b) => String(b.id) === String(selectedId));
    setEditForm((prev) => ({
      ...prev,
      brandId: selectedId || null,
      brandName: selectedBrand?.name || '',
    }));
  }, [brands]);

  const onInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handlePriceChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // Validate numeric input with decimals
    if (!value || value.match(/^\d{1,}(\.\d{0,2})?$/)) {
      setEditForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  }, []);

  const handleDiscountChange = useCallback((e) => {
    const { value } = e.target;
    
    // Validate percentage (0-100 with decimals)
    if (!value || (value.match(/^\d{1,}(\.\d{0,2})?$/) && parseFloat(value) <= 100)) {
      setEditForm((prev) => ({
        ...prev,
        discountPercent: value,
      }));
    }
  }, []);

  const handleStockChange = useCallback((e) => {
    const { value } = e.target;
    
    // Validate integer
    if (!value || value.match(/^\d+$/)) {
      setEditForm((prev) => ({
        ...prev,
        stockQuantity: value,
      }));
    }
  }, []);

  const onChangeImageHandler = useCallback((event) => {
    const image = event.target.files[0];
    if (image) {
      getBase64(image, (data) => {
        dispatch(actions.ImageUploaderMulty(JSON.stringify(data)));
      });
    }
  }, [dispatch]);

  const handleDeleteImage = useCallback((index) => {
    dispatch(actions.imageDeleteIndexArr(index));
  }, [dispatch]);

  const updatePriceAndStock = useCallback(async () => {
    if (!editForm.price || parseFloat(editForm.price) <= 0) {
      toast.error("Price must be greater than 0", {
        position: toast.POSITION.BOTTOM_CENTER,
      });
      return;
    }

    if (parseFloat(editForm.discountPercent) < 0 || parseFloat(editForm.discountPercent) > 100) {
      toast.error("Discount must be between 0 and 100", {
        position: toast.POSITION.BOTTOM_CENTER,
      });
      return;
    }

    const updateData = {
      price: parseFloat(editForm.price),
      discountPercent: parseFloat(editForm.discountPercent) || 0,
      stockQuantity: parseInt(editForm.stockQuantity) || 0,
    };

    try {
      await dispatch(actions.updateProductMerchant(productMerchantId, updateData));
      toast.success("Price and stock updated successfully", {
        position: toast.POSITION.BOTTOM_CENTER,
      });
    } catch (error) {
      toast.error("Failed to update price and stock", {
        position: toast.POSITION.BOTTOM_CENTER,
      });
    }
  }, [dispatch, productMerchantId, editForm]);

  const onUpdateProductHandler = useCallback(async () => {
    if (!editForm.name || editForm.name.trim() === "") {
      toast.error("Product name is required", {
        position: toast.POSITION.BOTTOM_CENTER,
      });
      return;
    }

    if (!editForm.categoryId || editForm.categoryId === 0) {
      toast.error("Please select a category", {
        position: toast.POSITION.BOTTOM_CENTER,
      });
      return;
    }

    // Prepare product update data
    const productUpdateData = {
      name: editForm.name,
      description: editForm.description || null,
      categoryId: parseInt(editForm.categoryId),
      brandId: editForm.brandId || null,
      unit: editForm.unit || "pcs",
      barcode: editForm.barcode || null,
      originCountry: editForm.origin || null,
      images: imgUrlArr.length > 0 ? imgUrlArr : null,
    };

    try {
      // Update Product details
      const productId = productMerchantDetails?.product?.id;
      if (productId) {
        await dispatch(actions.updateProduct(productId, productUpdateData));
      }

      // Update ProductMerchant details (price, discount, stock)
      const productMerchantUpdateData = {
        price: parseFloat(editForm.price),
        discountPercent: parseFloat(editForm.discountPercent) || 0,
        stockQuantity: parseInt(editForm.stockQuantity) || 0,
      };
      
      await dispatch(actions.updateProductMerchant(productMerchantId, productMerchantUpdateData));

      toast.success("Product updated successfully", {
        position: toast.POSITION.BOTTOM_CENTER,
      });

      // Reload product details
      await dispatch(actions.getProductMerchantDetails(productMerchantId));
      
      closeEditModal();
    } catch (error) {
      toast.error("Failed to update product", {
        position: toast.POSITION.BOTTOM_CENTER,
      });
    }
  }, [
    dispatch,
    productMerchantId,
    productMerchantDetails,
    editForm,
    imgUrlArr,
    closeEditModal,
  ]);

  // Render helpers
  const renderImageGallery = () => {
    if (!productMerchantDetails?.product?.images) return null;

    const images = Array.isArray(productMerchantDetails.product.images)
      ? productMerchantDetails.product.images
      : [];

    if (images.length === 0) return null;

    return images.map((image, index) => (
      <img
        key={index}
        src={image}
        alt={`Product thumbnail ${index + 1}`}
        className="smallGalleryImage"
        style={selectedImage === index ? { borderColor: "#FF263A" } : null}
        onClick={() => selectImage(index)}
      />
    ));
  };

  const renderDescription = () => {
    if (!productMerchantDetails?.product?.description) return null;

    const description = productMerchantDetails.product.description;
    const items = description.split(".").filter((item) => item.trim() !== "");

    return items.map((item, index) => <li key={index}>{item.trim()}.</li>);
  };

  const renderBrandsList = () => {
    return (
      <>
        <option value="">Select a brand (optional)</option>
        {brands.map((brand) => (
          <option key={brand.id} value={brand.id}>
            {brand.name}
          </option>
        ))}
      </>
    );
  };

  const renderCountriesList = () => {
    return (
      <>
        <option value="">Select country of origin</option>
        {COUNTRIES.map((country) => (
          <option key={country.code} value={country.name}>
            {country.name}
          </option>
        ))}
      </>
    );
  };

  const renderCategoriesList = () => {
    if (!categories || categories.length === 0) return null;

    return (
      <>
        <option key="empty" value={0}>
          Select category...
        </option>
        {categories.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
      </>
    );
  };

  const renderEditModalImages = () => {
    let imgTop = null;
    let hasItems = false;
    let imgSmall = [];

    if (imgUrlArr.length === 0) {
      imgTop = (
        <>
          <input
            accept="image/*"
            className="inputAvatarBox"
            id="ItemImg"
            type="file"
            onChange={onChangeImageHandler}
            disabled={loadingImg}
          />
          <label htmlFor="ItemImg">
            <div className="imgTopPlaceholder">
              {loadingImg ? <Spinner /> : <Add color="inherit" fontSize="large" />}
            </div>
          </label>
        </>
      );
    } else {
      hasItems = true;
      imgTop = (
        <div className="imageRemoveBig">
          <img src={imgUrlArr[0]} alt="Main product" />
          <div className="imageRemoveIcon" onClick={() => handleDeleteImage(0)}>
            <ClearIcon fontSize="small" color="inherit" />
          </div>
        </div>
      );

      imgUrlArr.forEach((item, index) => {
        if (index === 0) return;
        
        imgSmall.push(
          <div className="imgBoxListPlaceholder" key={index}>
            <img src={item} alt={`Product ${index + 1}`} />
            <div className="imageRemoveIconSmall" onClick={() => handleDeleteImage(index)}>
              <ClearIcon fontSize="inherit" color="inherit" />
            </div>
          </div>
        );
      });
    }

    return { imgTop, imgSmall, hasItems };
  };

  // Calculate price after discount
  const calculatePriceAfterDiscount = () => {
    if (!productMerchantDetails) return 0;
    const price = parseFloat(productMerchantDetails.price) || 0;
    const discount = parseFloat(productMerchantDetails.discountPercent) || 0;
    return (price - (discount * price) / 100).toFixed(2);
  };

  const calculateEditFormPriceAfterDiscount = () => {
    const price = parseFloat(editForm.price) || 0;
    const discount = parseFloat(editForm.discountPercent) || 0;
    return (price - (discount * price) / 100).toFixed(2);
  };

  // Loading state
  if (!productMerchantDetails && itemLoading) {
    return (
      <ErrorBoundary>
        <div className="ItemDetailsRoot">
          <div className="inventoryModalBox">
            <div className="AIMTitle">
              <span>Item details</span>
            </div>
            <div style={{ padding: "50px", display: "flex", justifyContent: "center" }}>
              <Spinner />
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  // No data state
  if (!productMerchantDetails && !itemLoading) {
    return (
      <ErrorBoundary>
        <div className="ItemDetailsRoot">
          <div className="inventoryModalBox">
            <div className="AIMTitle">
              <span>Item details</span>
            </div>
            <div style={{ padding: "50px", textAlign: "center" }}>
              <p>Product not found</p>
              <button onClick={() => navigate("/inventory")}>Back to Inventory</button>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  const product = productMerchantDetails?.product || {};
  const images = Array.isArray(product.images) ? product.images : [];
  const priceAfterDiscount = calculatePriceAfterDiscount();
  const editFormPriceAfterDiscount = calculateEditFormPriceAfterDiscount();
  const { imgTop, imgSmall, hasItems } = renderEditModalImages();

  return (
    <ErrorBoundary>
      <div className="ItemDetailsRoot">
        {/* Edit Modal */}
        {isEditModalOpen && (
          <div className="inventoryModal">
            <div className="inventoryModalBackdrop" onClick={closeEditModal}></div>
            <div className="itemDetailsEditModalPopup animated bounceIn">
              <div className="AIMTitle">
                <span>Edit item</span>
                <div onClick={closeEditModal}>
                  <ClearIcon fontSize="default" color="inherit" />
                </div>
              </div>
              <div className="itemDetailsEditModalContent">
                {/* Product Photos */}
                <div style={{ height: 290 }} className="addNewInputRow">
                  <span>Product photos*:</span>
                  <div style={{ width: "83%" }}>
                    <div className="addImgBox" style={{ display: "flex", justifyContent: "space-between" }}>
                      <div className="imgBoxTop1">{imgTop}</div>
                      <div className="imgBoxList" style={{ width: 100, flexDirection: "column", height: 300 }}>
                        {imgSmall}
                        {hasItems && (
                          <>
                            <input
                              accept="image/*"
                              className="inputAvatarBox"
                              id="ItemImgAdd"
                              type="file"
                              onChange={onChangeImageHandler}
                              disabled={loadingImg}
                            />
                            <label htmlFor="ItemImgAdd" className="ItemImg">
                              <div className="imgBoxListAdd">
                                {loadingImg ? <Spinner /> : <Add color="inherit" fontSize="small" />}
                              </div>
                            </label>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product Name */}
                <div className="addNewInputRow">
                  <span style={{ width: "120px", textAlign: "right" }}>Product name:</span>
                  <input type="text" name="name" value={editForm.name} onChange={onInputChange} />
                </div>

                {/* Barcode, Unit */}
                <div className="inputRows" style={{ paddingLeft: "70px" }}>
                  <div className="addNewInputRow">
                    <span>Barcode:</span>
                    <input
                      type="text"
                      name="barcode"
                      value={editForm.barcode}
                      onChange={onInputChange}
                      style={{ width: "150px", marginLeft: "10px" }}
                    />
                  </div>
                  <div className="addNewInputRow">
                    <span>Unit:</span>
                    <input
                      type="text"
                      name="unit"
                      value={editForm.unit}
                      onChange={onInputChange}
                      placeholder="pcs, kg, liters, etc."
                      style={{ width: "100px", marginLeft: "10px" }}
                    />
                  </div>
                </div>

                {/* Category */}
                <div className="addNewInputRow">
                  <span style={{ width: "120px", textAlign: "right" }}>Category:</span>
                  <select name="categoryId" value={editForm.categoryId} onChange={onInputChange}>
                    {renderCategoriesList()}
                  </select>
                </div>

                {/* Brand */}
                <div className="addNewInputRow">
                  <span style={{ width: "120px", textAlign: "right" }}>Brand:</span>
                  <select
                    value={editForm.brandId || ""}
                    onChange={handleBrandChange}
                  >
                    {renderBrandsList()}
                  </select>
                </div>

                {/* Country of Origin */}
                <div className="addNewInputRow">
                  <span style={{ width: "120px", textAlign: "right" }}>Country of origin:</span>
                  <select
                    name="origin"
                    value={editForm.origin}
                    onChange={onInputChange}
                  >
                    {renderCountriesList()}
                  </select>
                </div>

                {/* Description */}
                <div className="addNewInputRow noAlign">
                  <span style={{ width: "120px", textAlign: "right" }}>Description:</span>
                  <textarea
                    cols="30"
                    rows="10"
                    name="description"
                    value={editForm.description}
                    onChange={onInputChange}
                  ></textarea>
                </div>

                {/* Price, Discount, Stock */}
                <div style={{ marginTop: "20px", paddingLeft: "70px" }}>
                  <div className="inputRows">
                    <div className="addNewInputRow">
                      <span>Price (QAR):</span>
                      <input
                        type="text"
                        name="price"
                        value={editForm.price}
                        onChange={handlePriceChange}
                        style={{ width: "150px", marginLeft: "10px" }}
                      />
                    </div>
                    <div className="addNewInputRow">
                      <span>Discount (%):</span>
                      <input
                        type="text"
                        name="discountPercent"
                        value={editForm.discountPercent}
                        onChange={handleDiscountChange}
                        style={{ width: "150px", marginLeft: "10px" }}
                      />
                    </div>
                    <div className="addNewInputRow">
                      <span>Stock Quantity:</span>
                      <input
                        type="text"
                        name="stockQuantity"
                        value={editForm.stockQuantity}
                        onChange={handleStockChange}
                        style={{ width: "150px", marginLeft: "10px" }}
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: "10px", marginLeft: "120px" }}>
                    <span style={{ fontSize: "14px", color: "#6E7170" }}>
                      Price after discount: QAR {editFormPriceAfterDiscount}
                    </span>
                  </div>
                </div>

                {/* Save Button */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                  {updateButtonLoading ? (
                    <Spinner />
                  ) : (
                    <div className="itemDetailsEditModalButton" onClick={onUpdateProductHandler}>
                      Save changes
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="inventoryModalBox">
          <div className="AIMTitle">
            <span>Item details</span>
          </div>
          <div className="itemDetailsContent">
            {/* Single Column Layout */}
            <div className="leftColumn" style={{ width: "100%" }}>
              <div className="productInfoBox">
                {/* Image Gallery */}
                <div className="galleryBox" style={{ padding: 5 }}>
                  <img
                    src={images[selectedImage] || ""}
                    alt="Product main view"
                    className="mainGalleryImage"
                  />
                  <div className="galleryList">{renderImageGallery()}</div>
                </div>

                {/* Product Info */}
                <div style={{ padding: 5, paddingLeft: 50, position: "relative" }}>
                  <div className="itemDescriptionTitle">
                    <span style={{ fontSize: "18px" }}>{product.name}</span>
                  </div>
                  <div className="itemDescriptionPcs">
                    <div className="pcsInStock">In Stock</div>
                    <div className="pcsSold">
                      {productMerchantDetails.stockQuantity} / {product.unit || "pcs"}
                    </div>
                  </div>
                  <div className="itemDescriptionPrice">
                    <div className="descPriceNew">QAR {priceAfterDiscount}</div>
                    {productMerchantDetails.discountPercent > 0 && (
                      <>
                        <span className="descPriceOldLine">QAR {productMerchantDetails.price}</span>
                        <span className="descPriceOldPerc">
                          ({productMerchantDetails.discountPercent}% off)
                        </span>
                      </>
                    )}
                  </div>
                  {product.description && (
                    <div className="itemDescriptionAbout">
                      <span className="aboutTitle">About the Product: </span>
                      <ul>{renderDescription()}</ul>
                    </div>
                  )}
                  {product.originCountry && (
                    <div className="itemDescriptionExpDate">
                      <span>Country of origin:</span>
                      <span className="expDateBox">{product.originCountry}</span>
                    </div>
                  )}
                  {product.brand?.name && (
                    <div className="itemDescriptionExpDate">
                      <span>Brand:</span>
                      <span className="expDateBox">{product.brand.name}</span>
                    </div>
                  )}
                  {product.category?.name && (
                    <div className="itemDescriptionExpDate">
                      <span>Category:</span>
                      <span className="expDateBox">{product.category.name}</span>
                    </div>
                  )}
                  <div className="itemDetailsEditButton" onClick={openEditModal}>
                    Edit product details
                  </div>
                </div>
              </div>

              {/* Price and Stock Management */}
              <div className="priceDiscountBox">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span className="priceDiscSpan">Pricing and Stock</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "20px" }}>
                  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    <div className="priceInputBox">
                      <span>Price:</span>
                      <div className="priceDiscInput">
                        <input
                          type="text"
                          value={editForm.price}
                          name="price"
                          onChange={handlePriceChange}
                        />
                        <div>QAR</div>
                      </div>
                    </div>

                    <div className="priceInputBox">
                      <span>Discount (%):</span>
                      <div className="priceDiscInput">
                        <input
                          type="text"
                          value={editForm.discountPercent}
                          name="discountPercent"
                          onChange={handleDiscountChange}
                        />
                        <div>%</div>
                      </div>
                    </div>

                    <div className="priceInputBox">
                      <span>Stock Quantity:</span>
                      <div className="priceDiscInput">
                        <input
                          type="text"
                          value={editForm.stockQuantity}
                          name="stockQuantity"
                          onChange={handleStockChange}
                        />
                        <div>{product.unit || "pcs"}</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <div className="priceInputBox">
                      <div>
                        <span className="descPriceOldLine" style={{ marginLeft: 0, color: "#6E7170", fontSize: 15 }}>
                          QAR {editForm.price}
                        </span>
                        <span className="descPriceOldPerc" style={{ color: "#6E7170", fontSize: 15 }}>
                          ({editForm.discountPercent}% off)
                        </span>
                      </div>
                      <div className="descPriceNew" style={{ fontSize: 26 }}>
                        QAR {editFormPriceAfterDiscount}
                      </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                      <div className="addBatchButton">
                        {updateButtonLoading ? (
                          <Spinner />
                        ) : (
                          <div
                            style={{
                              height: "100%",
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                            }}
                            onClick={updatePriceAndStock}
                          >
                            Update Price & Stock
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default ItemDetails;
